/*
 * AdminPortalAccess — single pane of glass for every portal entry point.
 *
 * Sections:
 *   1. Portal cards (Buyer · Seller · Admin) with copy-URL + open-in-new-tab + user-count
 *   2. Storefront directory — every tenant's /s/{handle} URL + custom domain status
 *   3. Active sessions table — live user_sessions across all portals + kick controls
 *   4. "Launch as user" picker — search any user, 1-click open their portal pre-authed
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ShoppingBag, Store, Shield, Globe, ExternalLink, Copy, Loader2, Search,
  Users, Monitor, LogOut, Sparkles, AlertTriangle, CheckCircle2, Eye,
  RefreshCw, X, ArrowUpRight, Pause, Play, Wifi,
} from "lucide-react";
import api from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import { PageHeader } from "../../components/PortalShell";
import { toast } from "sonner";


const PORTAL_ICONS = { ShoppingBag, Store, Shield };

const TONE_BG = {
  sky:     "bg-sky-50 border-sky-200 text-sky-900",
  emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  violet:  "bg-violet-50 border-violet-200 text-violet-900",
};

const ROLE_DOT = {
  admin:  "bg-violet-500",
  buyer:  "bg-sky-500",
  seller: "bg-emerald-500",
};


export default function AdminPortalAccess() {
  const [dir, setDir] = useState(null);
  const [sessions, setSessions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessFilter, setSessFilter] = useState({ role: "", q: "" });

  const [launchOpen, setLaunchOpen] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [d, s] = await Promise.all([
        api.get("/admin/portal-access/directory"),
        api.get("/admin/portal-access/active-sessions"),
      ]);
      setDir(d.data);
      setSessions(s.data);
    } catch (e) {
      toast.error("Failed to load portal directory");
    } finally { setLoading(false); }
  };

  const refreshSessions = async () => {
    try {
      const params = new URLSearchParams();
      if (sessFilter.role) params.set("role", sessFilter.role);
      if (sessFilter.q)    params.set("q", sessFilter.q);
      const s = await api.get(`/admin/portal-access/active-sessions?${params}`);
      setSessions(s.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => { if (sessions) refreshSessions(); /* eslint-disable-next-line */ }, [sessFilter.role, sessFilter.q]);

  if (loading || !dir) {
    return (
      <div className="p-12 flex items-center justify-center" data-testid="admin-portal-access">
        <Loader2 className="w-5 h-5 animate-spin text-foreground/40"/>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="admin-portal-access">
      <PageHeader
        title="Portal Access Center"
        subtitle="Every portal entry · 1-click impersonation · live sessions · storefront directory"
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Tile label="Users total" value={dir.summary.users_total} icon={Users}/>
        <Tile label="Buyers"     value={dir.summary.buyers} icon={ShoppingBag} tone="sky"/>
        <Tile label="Sellers"    value={dir.summary.sellers} icon={Store} tone="emerald"/>
        <Tile label="Admins"     value={dir.summary.admins} icon={Shield} tone="violet"/>
        <Tile label="Storefronts" value={dir.summary.storefronts} icon={Globe}/>
        <Tile label="Live sessions (24h)" value={dir.summary.sessions_active} icon={Wifi} tone="ink"/>
      </div>

      {/* Portal cards */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center gap-2">
          <ExternalLink className="w-3.5 h-3.5"/>Portal entry points
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {dir.portals.map(p => (
            <PortalCard key={p.key} portal={p} origin={dir.origin}/>
          ))}
        </div>
      </div>

      {/* Launch as user */}
      <div className="rounded-2xl border bg-foreground text-background p-4 flex items-center justify-between" data-testid="launch-as-user-card">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5"/>
          <div>
            <div className="font-semibold">Launch portal as any user</div>
            <div className="text-[11px] opacity-80 mt-0.5">
              1-click impersonation: opens that user's portal in a new tab, pre-authenticated. Audit-logged + 4-eyes if write mode + impact ≥ $10K.
            </div>
          </div>
        </div>
        <Button variant="secondary" onClick={() => setLaunchOpen(true)} data-testid="launch-open-btn">
          <ArrowUpRight className="w-3.5 h-3.5 mr-1"/>Pick a user
        </Button>
      </div>

      {/* Storefront directory */}
      <div>
        <h3 className="text-sm font-semibold mb-2 text-foreground/80 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Globe className="w-3.5 h-3.5"/>Seller storefronts ({dir.storefronts.length})
          </span>
          <a href="/admin/domains"
             className="text-[11px] font-normal text-violet-700 hover:text-violet-900 hover:underline inline-flex items-center gap-1"
             data-testid="manage-domains-link">
            Manage domains <ArrowUpRight className="w-3 h-3"/>
          </a>
        </h3>
        <div className="rounded-2xl border bg-background overflow-x-auto" data-testid="storefront-directory">
          {dir.storefronts.length === 0 ? (
            <div className="px-3 py-8 text-center text-foreground/55 text-sm">
              No seller storefronts published yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                  <th className="px-3 py-2 font-medium">Seller</th>
                  <th className="px-3 py-2 font-medium">Public URL</th>
                  <th className="px-3 py-2 font-medium">Custom domain</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {dir.storefronts.slice(0, 50).map(s => (
                  <tr key={s.tenant_id} className="border-b last:border-0" data-testid={`storefront-${s.tenant_id}`}>
                    <td className="px-3 py-2 text-[12px]">
                      <div className="font-medium">{s.name}</div>
                      <div className="text-[10px] text-foreground/55 font-mono">@{s.handle}</div>
                    </td>
                    <td className="px-3 py-2 text-[11px] font-mono">
                      <a href={s.public_url} target="_blank" rel="noreferrer" className="hover:underline">
                        {s.public_url}
                      </a>
                    </td>
                    <td className="px-3 py-2 text-[11px]">
                      {s.custom_domain ? (
                        <a href={s.custom_url} target="_blank" rel="noreferrer" className="font-mono hover:underline text-violet-700">
                          {s.custom_domain}
                        </a>
                      ) : <span className="text-foreground/40">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      {s.is_paused ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] border bg-amber-100 border-amber-200 text-amber-800">
                          <Pause className="w-2.5 h-2.5 mr-1"/>Paused
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] border bg-emerald-100 border-emerald-200 text-emerald-700">
                          <Play className="w-2.5 h-2.5 mr-1"/>Live
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => copyToClipboard(s.public_url)}
                              data-testid={`storefront-copy-${s.tenant_id}`}>
                        <Copy className="w-3 h-3"/>
                      </Button>
                      <a href={s.public_url} target="_blank" rel="noreferrer"
                         className="inline-flex items-center h-7 px-2 text-[11px] rounded-md border bg-background hover:bg-foreground/5"
                         data-testid={`storefront-open-${s.tenant_id}`}>
                        <ExternalLink className="w-3 h-3 mr-1"/>Open
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Active sessions */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground/80 flex items-center gap-2">
            <Monitor className="w-3.5 h-3.5"/>Active sessions
            <span className="text-[10px] text-foreground/55 font-normal">
              ({sessions?.summary?.total || 0} total · {sessions?.summary?.buyer || 0} buyer · {sessions?.summary?.seller || 0} seller · {sessions?.summary?.admin || 0} admin)
            </span>
          </h3>
          <Button size="sm" variant="ghost" onClick={refreshSessions} data-testid="sessions-refresh">
            <RefreshCw className="w-3 h-3 mr-1"/>Refresh
          </Button>
        </div>
        <div className="rounded-2xl border bg-background p-3 mb-2 flex gap-2 items-end">
          <div className="flex-1">
            <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Search</Label>
            <Input className="h-8" placeholder="email · name · ip" value={sessFilter.q}
                    onChange={(e) => setSessFilter({ ...sessFilter, q: e.target.value })}
                    data-testid="sessions-search"/>
          </div>
          <div className="w-[140px]">
            <Label className="text-[10px] uppercase tracking-wider text-foreground/55">Role</Label>
            <select className="w-full h-8 border border-input rounded-md px-2 text-sm bg-background"
                    value={sessFilter.role} onChange={(e) => setSessFilter({ ...sessFilter, role: e.target.value })}
                    data-testid="sessions-role-filter">
              <option value="">All roles</option>
              <option value="buyer">Buyer</option>
              <option value="seller">Seller</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
        <div className="rounded-2xl border bg-background overflow-x-auto" data-testid="sessions-table">
          {(sessions?.rows || []).length === 0 ? (
            <div className="px-3 py-8 text-center text-foreground/55 text-sm">
              No active sessions in the last 72 hours.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-foreground/55 border-b">
                  <th className="px-3 py-2 font-medium">User</th>
                  <th className="px-3 py-2 font-medium">Role</th>
                  <th className="px-3 py-2 font-medium">IP</th>
                  <th className="px-3 py-2 font-medium">Agent</th>
                  <th className="px-3 py-2 font-medium">Last active</th>
                  <th className="px-3 py-2 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(sessions?.rows || []).slice(0, 100).map(r => (
                  <SessionRow key={r.session_id} row={r} onKick={refreshSessions}/>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <LaunchAsUserDialog open={launchOpen} onClose={() => setLaunchOpen(false)}/>
    </div>
  );
}


/* ─── Components ──────────────────────────────────────────────────────── */

function Tile({ label, value, icon: Icon, tone }) {
  const cls = tone === "ink"     ? "bg-foreground text-background border-foreground"
            : tone === "sky"     ? "bg-sky-50 border-sky-200 text-sky-900"
            : tone === "emerald" ? "bg-emerald-50 border-emerald-200 text-emerald-900"
            : tone === "violet"  ? "bg-violet-50 border-violet-200 text-violet-900"
            : "bg-background";
  return (
    <div className={`rounded-2xl border p-3 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70 flex items-center gap-1.5">
        <Icon className="w-3 h-3"/>{label}
      </div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function PortalCard({ portal, origin }) {
  const Icon = PORTAL_ICONS[portal.icon] || Globe;
  return (
    <div className={`rounded-2xl border p-4 ${TONE_BG[portal.tone] || "bg-background"}`}
         data-testid={`portal-card-${portal.key}`}>
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5"/>
          <div className="font-semibold text-[15px]">{portal.label}</div>
        </div>
        <span className="font-mono text-[10px] px-1.5 py-0.5 bg-background/70 border rounded">
          {portal.user_count} user{portal.user_count === 1 ? "" : "s"}
        </span>
      </div>
      <div className="text-[11px] opacity-75 mt-2 leading-relaxed">{portal.description}</div>
      <div className="mt-3 bg-background/80 border rounded-lg p-2 font-mono text-[11px] flex items-center justify-between gap-2">
        <span className="truncate" title={portal.url}>{portal.url}</span>
        <button onClick={() => copyToClipboard(portal.url)} className="hover:opacity-60" data-testid={`portal-copy-${portal.key}`}>
          <Copy className="w-3 h-3"/>
        </button>
      </div>
      <a href={portal.url} target="_blank" rel="noreferrer"
         className="mt-2 w-full inline-flex items-center justify-center h-8 px-3 text-[12px] font-medium rounded-md bg-background/90 border hover:bg-background"
         data-testid={`portal-open-${portal.key}`}>
        <ExternalLink className="w-3 h-3 mr-1"/>Open {portal.label}
      </a>
    </div>
  );
}

function SessionRow({ row, onKick }) {
  const [kicking, setKicking] = useState(false);
  const kick = async () => {
    if (!window.confirm(`Kick session for ${row.email}?`)) return;
    setKicking(true);
    try {
      await api.post(`/admin/portal-access/sessions/${row.session_id}/kick`);
      toast.success("Session revoked");
      onKick();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kick failed");
    } finally { setKicking(false); }
  };
  return (
    <tr className="border-b last:border-0" data-testid={`session-row-${row.session_id}`}>
      <td className="px-3 py-2 text-[12px]">
        <div className="font-medium">{row.name || row.email}</div>
        <div className="text-[10px] text-foreground/55">{row.email}</div>
      </td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1 text-[10.5px]">
          <span className={`w-1.5 h-1.5 rounded-full ${ROLE_DOT[row.role] || "bg-foreground/30"}`}/>
          {row.role || "—"}
        </span>
      </td>
      <td className="px-3 py-2 text-[11px] font-mono">{row.ip || "—"}</td>
      <td className="px-3 py-2 text-[10px] text-foreground/65 max-w-xs truncate" title={row.user_agent}>
        {row.user_agent || "—"}
      </td>
      <td className="px-3 py-2 text-[11px] text-foreground/65 whitespace-nowrap">
        {row.last_active_at ? new Date(row.last_active_at).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—"}
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="ghost" onClick={kick} disabled={kicking}
                className="text-rose-700 hover:text-rose-800" data-testid={`session-kick-${row.session_id}`}>
          {kicking ? <Loader2 className="w-3 h-3 animate-spin"/> : <LogOut className="w-3 h-3"/>}
        </Button>
      </td>
    </tr>
  );
}


function LaunchAsUserDialog({ open, onClose }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [mode, setMode] = useState("write");
  const [minutes, setMinutes] = useState(30);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`/admin/home/search?q=${encodeURIComponent(q)}`)
         .then(r => setResults((r.data?.users || []).slice(0, 8)))
         .catch(() => setResults([]))
         .finally(() => setSearching(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (open) { setQ(""); setResults([]); setPicked(null); setReason(""); setMode("write"); setMinutes(30); }
  }, [open]);

  const launch = async () => {
    if (!picked) { toast.error("Pick a user first"); return; }
    if (reason.trim().length < 3) { toast.error("Reason required (min 3 chars)"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/admin/portal-access/launch/${picked.id}`, {
        mode, minutes: Number(minutes), reason: reason.trim(),
      });
      // Open in a new tab
      window.open(r.data.launch_url, "_blank", "noopener");
      toast.success(`Launched ${picked.email}'s portal in a new tab · ${mode.toUpperCase()} mode · expires ${new Date(r.data.expires_at).toLocaleTimeString()}`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Launch failed");
    } finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="launch-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4"/>Launch portal as user
          </DialogTitle>
          <DialogDescription>
            Issues an impersonation token, opens that user's portal in a new tab pre-authenticated. Audit-logged. Read-only mode blocks all writes.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label>Search user</Label>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-foreground/40"/>
              <Input className="pl-8" placeholder="email · name · company"
                      value={q} onChange={(e) => { setQ(e.target.value); setPicked(null); }}
                      data-testid="launch-search-input"/>
            </div>
            {picked ? (
              <div className="mt-2 rounded-lg border bg-foreground/5 px-3 py-2 flex items-center justify-between" data-testid="launch-picked">
                <div className="flex items-center gap-2 text-[12px]">
                  <span className={`w-2 h-2 rounded-full ${ROLE_DOT[picked.role] || "bg-foreground/30"}`}/>
                  <div>
                    <div className="font-medium">{picked.email}</div>
                    <div className="text-[10px] text-foreground/55">{picked.role} · {picked.company || picked.name}</div>
                  </div>
                </div>
                <button onClick={() => setPicked(null)} className="hover:opacity-60"><X className="w-3.5 h-3.5"/></button>
              </div>
            ) : results.length > 0 && (
              <div className="mt-2 rounded-lg border bg-background max-h-48 overflow-y-auto" data-testid="launch-results">
                {results.map(r => (
                  <button key={r.id} onClick={() => setPicked(r)}
                          className="w-full text-left px-3 py-2 text-[12px] hover:bg-foreground/5 border-b last:border-0 flex items-center gap-2"
                          data-testid={`launch-pick-${r.id}`}>
                    <span className={`w-2 h-2 rounded-full ${ROLE_DOT[r.role] || "bg-foreground/30"}`}/>
                    <div className="flex-1">
                      <div>{r.email}</div>
                      <div className="text-[10px] text-foreground/55">{r.role} · {r.company || r.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            {searching && <div className="text-[10px] text-foreground/55 mt-1">Searching…</div>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Mode</Label>
              <select className="w-full h-9 border border-input rounded-md px-2 text-sm bg-background"
                      value={mode} onChange={(e) => setMode(e.target.value)} data-testid="launch-mode">
                <option value="write">Write (full access)</option>
                <option value="read">Read-only (safer)</option>
              </select>
            </div>
            <div>
              <Label>Duration (minutes)</Label>
              <Input type="number" min={1} max={240} value={minutes}
                      onChange={(e) => setMinutes(e.target.value)} data-testid="launch-minutes"/>
            </div>
          </div>
          <div>
            <Label>Reason <span className="text-rose-600">*</span></Label>
            <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="Why are you launching as this user? (audit-logged · min 3 chars)"
                      data-testid="launch-reason"/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={launch} disabled={busy || !picked || reason.trim().length < 3} data-testid="launch-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin"/>}
            <ExternalLink className="w-3.5 h-3.5 mr-1"/>Launch in new tab
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function copyToClipboard(text) {
  try {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  } catch {
    toast.error("Copy failed");
  }
}
